// Scoring model, analysis copy, and results DOM updates (depends on funnel-data.js: state.D, LOOP_*)
import {
  state,
  LOOP_DISPLAY_TITLES,
  BALANCED_MAIN_PATTERN,
  LOOP_DESC,
  TECHNIQUES,
  stripLeadingLoopProfileTitle,
  BODY_PHRASES,
  TIME_PHRASES
} from './funnel-data.js';
//
// Deterministic loop scoring: weighted accumulator (R–T + B) → primary/secondary slugs + six metrics (0–100)
// + TRT composite (thought-response strain, higher = more load) for meter/badge consistency.
// UI: Loop strength / Stickiness / Exit ability / Self-compassion / Awareness (meta) / Threat focus — each with hint line in HTML.
// loop strength = relational + work impact (c11+c12).
// stickiness = duration + hold time (c6+c16). Exit ability = how often you can step out (inverse of c1).
// self-compassion = blended 0–20 → %: inner tone (c10) + kinder self-talk capacity (c25) − blame/shame content (c7) − suppress habit (c5) − critic load (S bucket).
// Signal confidence: primaryLetter share of total points → profile_type blended when confidence < 0.35 (unless balanced override).

/** Letter buckets → LOOP_NAMES / MQI keys (rumination … night) */
export const LETTER_TO_SLUG = {
  R: 'rumination',
  W: 'anticipation',
  S: 'critic',
  A: 'scatter',
  N: 'shutdown',
  T: 'night',
  B: 'balanced'
};

/** Tie-break order when two letters share the same score */
export const LOOP_LETTERS = ['R', 'W', 'S', 'A', 'N', 'T'];

/** Intervention hints (deterministic mapping; optional for product use) */
var LOOP_INTERVENTIONS = {
  rumination: 'cognitive defusion + closure',
  anticipation: 'probability + grounding',
  critic: 'self-compassion reframing',
  scatter: 'attention anchoring',
  shutdown: 'behavioral activation',
  night: 'scheduled worry + sleep reset',
  balanced: 'resilience + optimization'
};

export function normalizeMetric(value, max) {
  if (max <= 0) return 0;
  return Math.min(100, Math.round((value / max) * 100));
}

/**
 * Weighted loop scores from onboarding answers (store keys c0… match mindlift-funnel.html).
 * c0 is down-weighted (+1) so the primary is driven by behavioral + emotional signals.
 */
export function accumulateLoopScores(data) {
  const scores = { R: 0, W: 0, S: 0, A: 0, N: 0, T: 0, B: 0 };
  const d = data || {};

  switch (d.c0) {
    case 'rumination':
      scores.R += 1;
      break;
    case 'anticipation':
      scores.W += 1;
      break;
    case 'critic':
      scores.S += 1;
      break;
    case 'scatter':
      scores.A += 1;
      break;
    case 'shutdown':
      scores.N += 1;
      break;
    case 'night':
      scores.T += 1;
      break;
    default:
      break;
  }

  const c2 = d.c2;
  if (c2 === 'conflict') scores.R += 2;
  if (c2 === 'decisions') scores.W += 2;
  if (c2 === 'night') {
    scores.T += 3;
    scores.R += 1;
  }
  if (c2 === 'transitions') scores.A += 2;
  if (c2 === 'constant') {
    scores.R += 1;
    scores.W += 1;
    scores.S += 1;
  }
  if (c2 === 'morning') scores.W += 1;

  const c5 = Array.isArray(d.c5) ? d.c5 : [];
  if (c5.includes('follow')) {
    scores.R += 3;
    scores.W += 2;
  }
  if (c5.includes('suppress')) {
    scores.S += 1;
    scores.N += 1;
  }
  if (c5.includes('distract')) scores.A += 2;
  if (c5.includes('nothing')) scores.N += 2;
  if (c5.includes('talk')) scores.W += 1;

  const c7 = d.c7;
  if (c7 === 'failure' || c7 === 'weak') scores.S += 3;
  if (c7 === 'danger') scores.W += 3;
  if (c7 === 'solve') {
    scores.R += 3;
    scores.W += 1;
  }
  if (c7 === 'noise') {
    scores.N += 2;
    scores.A += 1;
  }

  const c10 = d.c10;
  if (c10 === 'brutal') scores.S += 3;
  if (c10 === 'harsh') scores.S += 2;
  if (c10 === 'moderate') scores.S += 1;
  if (c10 === 'quiet') scores.B += 2;

  const c3 = d.c3;
  if (c3 === 'broken' || c3 === 'never') scores.T += 3;
  else if (c3 === 'hard') scores.T += 2;
  else if (c3 === 'varies') scores.T += 1;
  if (c3 === 'great') scores.B += 2;
  else if (c3 === 'ok') scores.B += 1;

  const c6 = parseInt(d.c6, 10) || 3;
  const c16 = parseInt(d.c16, 10) || 3;
  if (c6 >= 4) {
    scores.R += 2;
    scores.W += 1;
  }
  if (c16 >= 4) {
    scores.R += 2;
    scores.N += 1;
  }
  if (c6 <= 2 && c16 <= 2) scores.B += 2;

  const c27 = parseInt(d.c27, 10);
  if (c27 === 1) scores.W += 2;
  else if (c27 === 2) scores.W += 1;
  else if (c27 === 5) scores.B += 2;

  const c25 = d.c25;
  if (c25 === 'no') {
    scores.S += 1;
    scores.N += 1;
  }
  if (c25 === 'often') scores.B += 2;
  if (c25 === 'yes') scores.B += 3;

  return scores;
}

/**
 * Full profile: metrics, primary/secondary loop slugs, intensity, composite TRT strain.
 */
export function computeLoopProfile(data) {
  const d = data || {};
  const raw_scores = accumulateLoopScores(d);

  const c1 = parseInt(d.c1, 10) || 3;
  const c6 = parseInt(d.c6, 10) || 3;
  const c16 = parseInt(d.c16, 10) || 3;
  const c11 = parseInt(d.c11, 10) || 3;
  const c12 = parseInt(d.c12, 10) || 3;
  const c27 = parseInt(d.c27, 10) || 3;
  const c7 = d.c7;

  const loop_strength = normalizeMetric(c11 + c12, 10);
  const stickiness = normalizeMetric(c6 + c16, 10);

  const obsMap = { no: 1, sometimes: 2, often: 3, yes: 4 };
  const obs = obsMap[d.c25] != null ? obsMap[d.c25] : 2;
  const exit_ability = normalizeMetric(5 - c1, 4);

  const selfCompassionTonePts = { brutal: 0, harsh: 3, moderate: 7, quiet: 10 };
  const tonePts =
    selfCompassionTonePts[d.c10] != null ? selfCompassionTonePts[d.c10] : 6;

  // Keep penalties bounded so this metric doesn't collapse to 0 too easily.
  let blamePts = 0;
  if (c7 === 'failure' || c7 === 'weak') blamePts -= 5;
  else if (c7 === 'danger') blamePts -= 2;

  const selfCompassionObsPts = { no: 0, sometimes: 3, often: 5, yes: 6 };
  const obsPtsSelf =
    selfCompassionObsPts[d.c25] != null ? selfCompassionObsPts[d.c25] : 4;

  const c5list = Array.isArray(d.c5) ? d.c5 : [];
  let suppressPts = 0;
  if (c5list.includes('suppress')) suppressPts -= 2;

  const criticLoad = raw_scores.S || 0;
  const criticPen = Math.min(4, Math.round(criticLoad * 0.35));

  let selfCompassionRaw = tonePts + blamePts + obsPtsSelf + suppressPts - criticPen;
  selfCompassionRaw = Math.max(0, Math.min(20, selfCompassionRaw));
  const self_compassion = normalizeMetric(selfCompassionRaw, 20);

  const metacognition = normalizeMetric(obs, 4);

  const dangerBonus = c7 === 'danger' ? 2 : 0;
  const threat_bias = normalizeMetric(6 - c27 + dangerBonus, 7);

  // Intensity follows stickiness (how long loops run / hard to release), not day-to-day impact alone.
  let intensity = 'light';
  if (stickiness > 75) intensity = 'high';
  else if (stickiness > 50) intensity = 'moderate';

  const loopEntries = Object.entries(raw_scores).filter(function (x) {
    return x[0] !== 'B';
  });
  loopEntries.sort(function (a, b) {
    if (b[1] !== a[1]) return b[1] - a[1];
    return LOOP_LETTERS.indexOf(a[0]) - LOOP_LETTERS.indexOf(b[0]);
  });

  let primaryLetter = loopEntries[0][0];
  let secondaryLetter = loopEntries[1][0];

  const total =
    raw_scores.R +
    raw_scores.W +
    raw_scores.S +
    raw_scores.A +
    raw_scores.N +
    raw_scores.T +
    raw_scores.B;
  const confidence = total > 0 ? raw_scores[primaryLetter] / total : 0;

  const loadMax = Math.max(loop_strength, stickiness);
  let balanced_override =
    loadMax < 40 && exit_ability > 60 && self_compassion > 60 && metacognition > 60;

  let primary_loop = LETTER_TO_SLUG[primaryLetter];
  let secondary_loop = LETTER_TO_SLUG[secondaryLetter];

  if (balanced_override) {
    primary_loop = 'balanced';
    secondary_loop = LETTER_TO_SLUG[loopEntries[0][0]];
  }

  let profile_type;
  if (balanced_override) {
    profile_type = 'balanced';
  } else if (confidence < 0.35) {
    profile_type = 'blended';
  } else {
    profile_type = 'distinct';
  }

  const composite = Math.min(
    95,
    Math.max(
      5,
      Math.round(
        loop_strength * 0.18 +
          stickiness * 0.20 +
          (100 - exit_ability) * 0.20 +
          (100 - self_compassion) * 0.16 +
          (100 - metacognition) * 0.12 +
          threat_bias * 0.14
      )
    )
  );

  return {
    primary_loop: primary_loop,
    secondary_loop: secondary_loop,
    intensity: intensity,
    metrics: {
      loop_strength: loop_strength,
      stickiness: stickiness,
      exit_ability: exit_ability,
      self_compassion: self_compassion,
      metacognition: metacognition,
      threat_bias: threat_bias
    },
    raw_scores: raw_scores,
    balanced_override: balanced_override,
    composite: composite,
    confidence: confidence,
    profile_type: profile_type,
    intervention_primary: LOOP_INTERVENTIONS[primary_loop] || '',
    intervention_secondary: LOOP_INTERVENTIONS[secondary_loop] || ''
  };
}

function computeScores() {
  const p = computeLoopProfile(state.D);
  return {
    loop: p.metrics.loop_strength,
    stick: p.metrics.stickiness,
    exit: p.metrics.exit_ability,
    comp: p.metrics.self_compassion,
    meta: p.metrics.metacognition,
    threat: p.metrics.threat_bias,
    composite: p.composite,
    profile: p
  };
}

/** Balanced Mind UI: deterministic override or low TRT composite (legacy band). */
export function isBalancedMindProfile() {
  const p = computeLoopProfile(state.D);
  return p.primary_loop === 'balanced' || p.composite < 35;
}

function buildInterventionType() {
  const resp = Array.isArray(state.D.c5) ? state.D.c5 : [];
  if (resp.includes('follow')) return 'Loop interrupt';
  if (resp.includes('suppress')) return 'Defusion technique';
  if (resp.includes('distract')) return 'Anchor + redirect';
  if (resp.includes('talk')) return 'Perspective + reappraisal';
  if (resp.includes('nothing')) return 'Activation technique';
  return 'In-the-moment CBT';
}

function setBar(k, v) {
  document.getElementById('bar-' + k).style.width = v + '%';
  document.getElementById('bv-' + k).textContent = v + '%';
}

/** Reorder App Store / Play badges so the visitor’s platform is first (higher tap rate). */
function reorderStoreBadges() {
  const wrap = document.getElementById('store-badges-row');
  if (!wrap) return;
  const ios = wrap.querySelector('.store-badge--ios');
  const gp = wrap.querySelector('.store-badge--gp');
  if (!ios || !gp) return;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  if (isAndroid && !isIOS) {
    wrap.insertBefore(gp, ios);
  } else {
    wrap.insertBefore(ios, gp);
  }
}

/** Personalised copy on the app download block (result screen). */
function setPlanDownloadBlock(balanced, firstName) {
  const eyebrow = document.getElementById('plan-dl-eyebrow');
  const title = document.getElementById('plan-dl-title');
  const sub = document.getElementById('plan-dl-sub');
  const trust = document.getElementById('plan-dl-trust');
  const n = firstName && String(firstName).trim() ? String(firstName).trim() : '';

  if (eyebrow) {
    eyebrow.textContent = balanced ? 'Keep your progress' : 'Start your training';
  }
  if (title) {
    title.textContent = balanced
      ? 'Get the app — stay on track with your profile'
      : 'Download MindLift — your plan is inside';
  }
  if (sub) {
    if (balanced) {
      sub.textContent = n
        ? `${n}, short daily check-ins in the app keep your plan aligned with how you’re doing — same personalisation you just saw.`
        : 'Short daily check-ins in the app keep your plan aligned with how you’re doing — same personalisation you just saw.';
    } else {
      sub.textContent = n
        ? `Hi ${n} — open the app to start your daily sessions. Everything you reviewed here is ready on your phone.`
        : 'Open the app to start your daily sessions — everything you reviewed here is ready on your phone.';
    }
  }
  if (trust) {
    trust.innerHTML =
      '<strong>Free</strong> · 4.8★ · 65K+ people · <strong>No credit card</strong>';
  }

  reorderStoreBadges();
}

/** Personalise spec §5: first two picked BODY_PHRASES joined with "and"; 3+ picks -> first two + "and more". 'none'/missing -> ''. */
function bodyPhrase(c13) {
  const picks = Array.isArray(c13) ? c13.filter((v) => v && v !== 'none') : [];
  if (!picks.length) return '';
  const phrases = picks.slice(0, 2).map((v) => BODY_PHRASES[v]).filter(Boolean);
  if (!phrases.length) return '';
  // Avoid the repeated verb when both picks are phrased as "lands ..." (e.g. chest + stomach
  // would otherwise read "lands as a tight chest and lands in your stomach") — strip the
  // leading "lands " from the second phrase so it reads "...and in your stomach" instead.
  let second = phrases[1];
  if (second && phrases[0].indexOf('lands ') === 0 && second.indexOf('lands ') === 0) {
    second = second.slice('lands '.length);
  }
  const joined = phrases.length === 1 ? phrases[0] : phrases[0] + ' and ' + second;
  return picks.length > 2 ? joined + ' and more' : joined;
}

/** Personalise spec §5: the ms1/#a-pattern-tile sentence. Either half may be absent; both absent -> ''. */
export function patternSentence(D) {
  const d = D || {};
  const body = bodyPhrase(d.c13);
  const time = d.c2 && TIME_PHRASES[d.c2] ? TIME_PHRASES[d.c2] : '';
  if (body && time) return "For you it " + body + " and shows up most " + time + " — that's the pattern we'll train around.";
  if (body) return "For you it " + body + " — that's where we'll start.";
  if (time) return "For you it shows up most " + time + " — that's when we'll train.";
  return '';
}

/** Generic "a, b and c" joiner used by triedHeadline() and reusable anywhere a picked-list needs prose joining. */
export function joinWithAnd(items) {
  const arr = (items || []).filter(Boolean);
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return arr[0] + ' and ' + arr[1];
  return arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
}

const TRIED_LABELS = { meditation: 'meditation', therapy: 'therapy', apps: 'wellness apps', exercise: 'exercise', none: 'not much' };

/** Personalise spec §4: react-tried's headline. picks is state.D.c8 (array, answer order). */
export function triedHeadline(picks) {
  const arr = (picks || []).filter(Boolean);
  if (!arr.length || (arr.length === 1 && arr[0] === 'none')) {
    return "You haven't tried much yet — good.";
  }
  const labels = arr.map((v) => TRIED_LABELS[v] || v);
  return "You’ve tried " + joinWithAnd(labels) + '. Here’s why the loop is still here.';
}

export function buildAnalysis() {
  const sc = computeScores();
  const profile = sc.profile;
  const name = (state.D.name || '').split(' ')[0];
  const loopKey = profile.primary_loop === 'balanced' ? 'balanced' : profile.primary_loop;
  const n = name;
  const mMap = { 5: '5 min/day', 10: '10 min/day', 20: '20 min/day', 30: '30+ min/day' };
  const dMap = { 5: '28 days', 10: '21 days', 20: '14 days', 30: '10 days' };

  const showBalanced = profile.primary_loop === 'balanced' || sc.composite < 35;

  const ah = document.getElementById('a-hed');
  if (ah) ah.textContent = (n ? n + "'s" : 'Your') + ' Loop Profile';
  const as = document.getElementById('a-sub');
  if (as) {
    as.textContent = n
      ? "Most apps calm the mind. " + n + "'s plan trains how to respond — from your answers"
      : 'Most apps calm the mind. This plan trains how you respond — from your answers';
  }

  setTimeout(function () {
    setBar('loop', sc.loop);
    setBar('stick', sc.stick);
    setBar('exit', sc.exit);
    setBar('comp', sc.comp);
    setBar('meta', sc.meta);
    setBar('threat', sc.threat);
  }, 220);

  var mainLoopDisplay = showBalanced
    ? LOOP_DISPLAY_TITLES.balanced
    : LOOP_DISPLAY_TITLES[loopKey] || '—';

  var LOOP_EMBLEMS = { rumination: '🔁', anticipation: '🌀', critic: '📢', scatter: '💨', shutdown: '🪨', night: '🌙', balanced: '🌿' };
  var emblemKey = showBalanced ? 'balanced' : loopKey;
  var liEmblem = document.getElementById('li-emblem');
  if (liEmblem) liEmblem.textContent = LOOP_EMBLEMS[emblemKey] || '🧠';
  var liName = document.getElementById('li-name');
  if (liName) liName.textContent = mainLoopDisplay;
  var period = dMap[state.D.c21] || '21 days';
  var daily = mMap[state.D.c21] || '10 min/day';
  var technique = TECHNIQUES[loopKey] || 'CBT';
  var primaryHed = 'Main pattern in your loop profile';
  var loopDescHtml = stripLeadingLoopProfileTitle(
    showBalanced ? BALANCED_MAIN_PATTERN : LOOP_DESC[loopKey] || LOOP_DESC.rumination
  );

  function setEl(id, fn) {
    var el = document.getElementById(id);
    if (el) fn(el);
  }
  setEl('r-loop', function (el) {
    el.textContent = mainLoopDisplay;
  });
  setEl('a-r-loop', function (el) {
    el.textContent = mainLoopDisplay;
  });
  setEl('r-period', function (el) {
    el.textContent = period;
  });
  setEl('a-r-period', function (el) {
    el.textContent = period;
  });
  setEl('a-r-daily', function (el) {
    el.textContent = daily;
  });
  setEl('r-daily-note', function (el) {
    el.textContent = 'Daily session: ' + daily;
  });
  setEl('r-technique', function (el) {
    el.textContent = technique;
  });
  setEl('a-r-technique', function (el) {
    el.textContent = technique;
  });
  setEl('r-delivery', function (el) {
    el.textContent = 'In-the-moment';
  });
  var REASON_PHRASES = { work: 'the pressure at work', relationship: 'the relationship strain', transition: 'a big life transition', sleep: 'better nights', enough: 'a long-running pattern', noticed: 'the pattern you noticed' };
  var RESET_LABELS = { stepback: 'Stepping back from a thought', hypothesis: 'Checking a worry like a hypothesis', selftalk: 'Believable self-talk', focus: 'Focus resets', tinystep: 'Tiny next steps', winddown: 'Night wind-down' };
  setEl('r-hero-eyebrow', function (el) {
    var reason = REASON_PHRASES[state.D.csq1];
    if (n && reason) el.textContent = n + "\u2019s plan \u2014 for " + reason;
    else if (reason) el.textContent = 'Your plan \u2014 for ' + reason;
  });
  setEl('r-your-resets', function (el) {
    var picks = Array.isArray(state.D.csq2) ? state.D.csq2.filter(function (v) { return RESET_LABELS[v]; }) : [];
    if (!picks.length) return;
    el.textContent = 'Starting with your picks: ' + picks.slice(0, 2).map(function (v) { return RESET_LABELS[v]; }).join(' \u00b7 ');
    el.removeAttribute('hidden');
  });
  setEl('r-hero-sub', function (el) {
    if (showBalanced) {
      el.textContent = "You already have some balance, we'll help you keep it.";
    } else if (profile.profile_type === 'blended') {
      var blendP = LOOP_DISPLAY_TITLES[profile.primary_loop] || 'this pattern';
      var blendS = LOOP_DISPLAY_TITLES[profile.secondary_loop] || 'another pattern';
      el.textContent = 'Your mind leans toward ' + blendP + ', with some ' + blendS + '.';
    } else {
      el.textContent = (LOOP_DISPLAY_TITLES[loopKey] || 'Your pattern') + ' — your plan reflects this.';
    }
  });
  setEl('a-primary-hed', function (el) {
    el.textContent = primaryHed;
  });
  setEl('a-loop-desc', function (el) {
    el.innerHTML = loopDescHtml;
  });
  setEl('a-pattern-tile', function (el) {
    const sentence = patternSentence(state.D);
    el.textContent = sentence;
    el.hidden = !sentence;
  });

  const meter = document.getElementById('r-meter');
  if (meter) meter.style.left = Math.min(sc.composite, 88) + '%';

  const badge = document.getElementById('r-badge');
  if (badge) {
    if (sc.composite >= 60) {
      badge.textContent = 'Loop-controlled';
      badge.style.cssText =
        'padding:4px 12px;border-radius:99px;font-size:12px;font-weight:700;background:#ffe5e5;color:#e04545';
    } else if (sc.composite >= 35) {
      badge.textContent = 'Moderate';
      badge.style.cssText =
        'padding:4px 12px;border-radius:99px;font-size:12px;font-weight:700;background:#fff9ee;color:#d98a0a';
    } else {
      badge.textContent = 'Balanced Mind profile';
      badge.style.cssText =
        'padding:4px 12px;border-radius:99px;font-size:12px;font-weight:700;background:#edf9f3;color:#26a96c';
    }
  }

  setPlanDownloadBlock(showBalanced, n);
}

